import React from 'react'
import { Button } from '@/components/ui/button'
import { AlertCircle, Clock, CheckCircle2, ArrowRight } from 'lucide-react'

export interface ActionBannerProps {
  title: string
  description: string
  actionButton?: string | null
  onAction?: () => void
  variant: 'success' | 'waiting' | 'action' | 'unknown'
}

const variantStyles = {
  success: {
    bg: 'bg-[#C0FD72]/20 border-[#C0FD72]',
    icon: <CheckCircle2 className="h-6 w-6 text-[#C0FD72]" />,
    iconBg: 'bg-[#C0FD72]/30',
    titleColor: 'text-white',
    descColor: 'text-gray-300',
    buttonClass: 'bg-[#C0FD72] hover:bg-[#C0FD72]/90 text-black',
  },
  waiting: {
    bg: 'bg-blue-500/10 border-blue-500/50',
    icon: <Clock className="h-6 w-6 text-blue-400" />,
    iconBg: 'bg-blue-500/20',
    titleColor: 'text-white',
    descColor: 'text-gray-300',
    buttonClass: 'bg-blue-500 hover:bg-blue-600 text-white',
  },
  action: {
    bg: 'bg-amber-500/10 border-amber-400',
    icon: <AlertCircle className="h-6 w-6 text-amber-400" />,
    iconBg: 'bg-amber-500/20',
    titleColor: 'text-white',
    descColor: 'text-gray-300',
    buttonClass: 'bg-[#C0FD72] hover:bg-[#C0FD72]/90 text-black',
  },
  unknown: {
    bg: 'bg-gray-500/10 border-gray-500/50',
    icon: <AlertCircle className="h-6 w-6 text-gray-400" />,
    iconBg: 'bg-gray-500/20',
    titleColor: 'text-white',
    descColor: 'text-gray-400',
    buttonClass: 'bg-gray-600 hover:bg-gray-700 text-white',
  },
}

export const ActionBanner: React.FC<ActionBannerProps> = ({
  title,
  description,
  actionButton,
  onAction,
  variant,
}) => {
  const styles = variantStyles[variant] || variantStyles.unknown

  return (
    <div className={`rounded-xl border-2 p-4 ${styles.bg}`}>
      <div className="flex items-start gap-4">
        <div className={`p-2 rounded-full ${styles.iconBg} shrink-0`}>{styles.icon}</div>
        <div className="flex-1 min-w-0">
          <h3 className={`font-semibold text-lg ${styles.titleColor}`}>{title}</h3>
          <p className={`mt-1 text-sm ${styles.descColor}`}>{description}</p>
        </div>
        {actionButton && onAction && (
          <Button onClick={onAction} className={`shrink-0 ${styles.buttonClass}`}>
            {actionButton}
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        )}
      </div>
    </div>
  )
}

export default ActionBanner
